import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    OutlinedInput,
    Button,
} from "@mui/material";
import { X, UploadCloud, FileText, Trash2 } from "lucide-react";
import { setSelectedProjectChat, uploadFiles } from '../../lib/redux/actions';
import toast from '../../lib/utils/toast';
import useLoader from "../../hooks/useLoader";
import { useDispatch } from "react-redux";

const agents = [
    { id: "generic", label: "Generic" },
    { id: "marketing_insights", label: "Marketing Insights" },
    { id: "analytics_financial", label: "Analytics Financial" },
];

// Map UI agent IDs to backend API format
const AGENT_TYPE_MAP = {
    generic: "generic",
    marketing_insights: "marketing",
    analytics_financial: "finance",
};

export default function CreateProjectDialog({ open, onClose, onProjectCreated, fetchRecentChats, setProjectChats }) {
    const navigate = useNavigate();
    const [projectName, setProjectName] = useState("");
    const [selectedAgentId, setSelectedAgentId] = useState("generic");
    const [loading, setLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const fileInputRef = useRef(null);
    const progressIntervals = useRef({});
    const { showLoader, hideLoader } = useLoader();
    const dispatch = useDispatch();
    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        const validFiles = validateFiles(selectedFiles);

        if (validFiles.length === 0) {
            if (selectedFiles.length > 0) event.target.value = ""; // Reset input if all files were invalid
            return;
        }

        const newFiles = validFiles.map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            progress: 0,
            status: 'pending', // pending, uploading, completed, error
            uploadedUrl: null
        }));
        setFiles(prev => [...prev, ...newFiles]);
        uploadFilesToS3(newFiles);
    };

    const validateFiles = (filesToValidate) => {
        let allowedExtensions = [];


        if (selectedAgentId === "analytics_financial") {
            allowedExtensions = ['.csv', '.xlsx'];
        } else if (selectedAgentId === "generic") {
            allowedExtensions = ['.pdf', '.docx', '.csv', '.txt', '.xls', '.xlsx'];
        }

        const validFiles = filesToValidate.filter(file => {
            const fileName = file.name.toLowerCase();
            const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

            if (selectedAgentId === "analytics_financial") {
                if (!allowedExtensions.includes(fileExtension)) {
                    toast.error(`File "${file.name}" is not supported. Only CSV and XLSX files are allowed.`);
                    return false;
                }
            } else if (selectedAgentId === "generic") {
                if (!allowedExtensions.includes(fileExtension)) {
                    toast.error(`File "${file.name}" is not supported. Supported formats: .pdf, .docx, .csv, .txt, .xls, .xlsx`);
                    return false;
                }
            }
            return true;
        });

        return validFiles;
    };

    const uploadFilesToS3 = async (newFiles) => {
        try {
            // Start progress animation from 0 to 80% over 5 seconds for ALL files
            const progressDuration = 5000; // 5 seconds
            const progressTarget = 80; // Stop at 80%
            const intervalTime = 100; // Update every 100ms
            const progressIncrement = (progressTarget / progressDuration) * intervalTime;

            let currentProgress = 0;
            const progressIntervalId = setInterval(() => {
                currentProgress += progressIncrement;
                if (currentProgress >= progressTarget) {
                    clearInterval(progressIntervalId);
                    currentProgress = progressTarget;
                }
                // Update progress for all files being uploaded
                setFiles(prev => prev.map(f => {
                    const isBeingUploaded = newFiles.some(nf => nf.id === f.id);
                    return isBeingUploaded ? { ...f, progress: currentProgress } : f;
                }));
            }, intervalTime);

            // Store interval ID for cleanup
            progressIntervals.current['batch'] = progressIntervalId;

            // Upload ALL files in a single batch API call
            const formData = new FormData();
            newFiles.forEach((fileObj) => {
                formData.append("files", fileObj.file);
            });

            const result = await uploadFiles(formData);

            // Clear interval and set to 100% on success
            if (progressIntervals.current['batch']) {
                clearInterval(progressIntervals.current['batch']);
                delete progressIntervals.current['batch'];
            }

            if (result.statusCode === 200 && result.data && result.data.length > 0) {
                // Map each uploaded URL to the corresponding file
                setFiles(prev => prev.map(f => {
                    const fileIndex = newFiles.findIndex(nf => nf.id === f.id);
                    if (fileIndex !== -1 && result.data[fileIndex]) {
                        return {
                            ...f,
                            progress: 100,
                            status: 'completed',
                            uploadedUrl: result.data[fileIndex]
                        };
                    }
                    return f;
                }));
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            // Clear interval on error
            if (progressIntervals.current['batch']) {
                clearInterval(progressIntervals.current['batch']);
                delete progressIntervals.current['batch'];
            }

            // Mark all files as error
            setFiles(prev => prev.map(f => {
                const isFailedFile = newFiles.some(nf => nf.id === f.id);
                return isFailedFile ? { ...f, progress: 0, status: 'error' } : f;
            }));

            toast.error(`Failed to upload files`);
            console.error('File upload error:', error);
        }
    };

    const handleRemoveFile = (id) => {
        setFiles(prev => prev.filter(f => f.id !== id));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = validateFiles(droppedFiles);

        if (validFiles.length === 0) return;

        const newFiles = validFiles.map(file => ({
            file,
            id: Math.random().toString(36).substr(2, 9),
            progress: 0,
            status: 'pending',
            uploadedUrl: null
        }));
        setFiles(prev => [...prev, ...newFiles]);
        uploadFilesToS3(newFiles);
    };

    const handleCreate = async () => {
        if (!projectName.trim()) return;

        setLoading(true);
        showLoader()
        try {
            // Prepare payload for backend API
            const payload = {
                project_name: projectName,
                project_type: AGENT_TYPE_MAP[selectedAgentId] || "PROJECT", // Map to backend format
                files: files
                    .filter(f => f.status === 'completed')
                    .map(f => ({
                        fileUrl: f.uploadedUrl,
                        fileName: f.file.name
                    }))
            };

            const result = await onProjectCreated(payload);
            if (!result) {
                throw new Error("Failed to create project");
            }
            const { project } = result;
            if (project && project?._id && project?.projectName !== "marketing") {
                handleClose();
                navigate(`/project/${encodeURIComponent(project.projectName)}/${encodeURIComponent(project.projectType)}/${project._id}`);
            } else {
                handleClose();
                navigate(`/project/${encodeURIComponent(project.projectName)}/marketing/${project._id}`);
            }
            dispatch(setSelectedProjectChat(null))
            setProjectChats([])
            fetchRecentChats()
        } catch (error) {
            console.error("Failed to create project:", error);

            if (error?.message?.includes("duplicate key")) {
                toast.error("A project with this name already exists.");
            } else {
                toast.error(error?.message || "Failed to create project");
            }
        } finally {
            hideLoader()
            setLoading(false);
        }
    };

    const handleClose = () => {
        setProjectName("");
        setSelectedAgentId("generic");
        setFiles([]);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                style: { borderRadius: 16, padding: "32px", maxWidth: "600px" },
            }}
        >
            <div className="relative mb-8 text-center">
                <Typography variant="h5" style={{ fontWeight: 400, color: "#000", fontSize: "24px" }}>
                    Create Project
                </Typography>
                <IconButton onClick={handleClose} style={{ position: "absolute", right: -16, top: -20 }}>
                    <X className="w-6 h-6 text-gray-500" />
                </IconButton>
            </div>

            <DialogContent className="p-0 overflow-visible flex flex-col gap-6">
                {/* Project Name Input */}
                <OutlinedInput
                    placeholder="Enter your project name"
                    fullWidth
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="bg-white truncate pt-0.3"
                    sx={{
                        "& fieldset": { borderColor: "#E0E0E0", borderRadius: "12px" },
                        "&.Mui-focused fieldset": { borderColor: "#1B5C29" },
                        input: {
                            padding: "12px 10px",
                            "&::placeholder": {
                                color: "#9CA3AF",
                                opacity: 1,
                                fontSize: "14px",
                                fontWeight: 400,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                paddingTop: "20px"
                            }
                        }
                    }}
                />

                {/* Agents Selection */}
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-black whitespace-nowrap min-w-16">Agents</span>
                    <div className="flex flex-wrap gap-2">
                        {agents.map((agent) => {
                            const isSelected = selectedAgentId === agent.id;
                            return (
                                <button
                                    key={agent.id}
                                    onClick={() => setSelectedAgentId(agent.id)}
                                    className={`px-4 h-full  min-h-[25px] rounded-full border transition-colors flex text-sm ${isSelected
                                        ? "bg-[#1B5C29] text-white border-[#0A5C36]"
                                        : "bg-[#1B5C2914] text-[#1B5C29] border-[#1B5C29]"
                                        }`}
                                >
                                    <span className="truncate pt-1">
                                        {agent.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>


                {/* Drag & Drop Area */}
                {selectedAgentId !== "marketing_insights" && (
                    <div
                        className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center mb-6 cursor-pointer hover:bg-gray-50 transition-colors"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            type="file"
                            multiple
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleFileChange}
                            accept={
                                selectedAgentId === "analytics_financial"
                                    ? ".csv, .xlsx"
                                    : selectedAgentId === "generic"
                                        ? ".pdf, .docx, .csv, .txt, .xls, .xlsx"
                                        : ""
                            }
                        />
                        <UploadCloud className="w-10 h-10 text-green-700 mb-3" />
                        <Typography variant="body1" className="text-gray-900 font-medium">
                            <span className="text-green-700 underline">Choose a file</span> or drag and drop it here
                        </Typography>
                        {selectedAgentId === "analytics_financial" && (
                            <Typography variant="caption" className="text-gray-500 mt-2">
                                Supported formats: CSV, XLSX
                            </Typography>
                        )}
                        {selectedAgentId === "generic" && (
                            <Typography variant="caption" className="text-gray-500 mt-2">
                                Supported formats: PDF, DOCX, CSV, TXT, XLS, XLSX
                            </Typography>
                        )}
                    </div>
                )}

                {/* File List */}
                <div className="flex flex-col gap-3 mb-6">
                    {files.map((fileObj) => (
                        <div key={fileObj.id} className="border border-gray-100 rounded-lg p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                                <div className="bg-red-50 p-2 rounded">
                                    <FileText className="w-6 h-6 text-red-500" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-1">
                                        <Typography variant="body2" className="font-medium text-gray-900 truncate algin-left sm:max-w-[200px] max-w-[100px] ">
                                            {fileObj.file.name}
                                        </Typography>
                                        {fileObj.status === 'completed' ? (
                                            <div className="flex items-center gap-2">

                                                <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                                                    Completed
                                                </span>
                                                <IconButton size="small" onClick={() => handleRemoveFile(fileObj.id)}>
                                                    <Trash2 className="w-4 h-4 text-red-400" />
                                                </IconButton>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-gray-500">
                                                    Uploading...
                                                </span>
                                                <IconButton size="small" onClick={() => handleRemoveFile(fileObj.id)}>
                                                    <X className="w-4 h-4 text-gray-400" />
                                                </IconButton>
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                                        <div
                                            className="bg-green-600 h-1.5 rounded-full"
                                            style={{ width: `${fileObj.progress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Create Button */}
                <div className="flex justify-end mt-4">
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={
                            !projectName.trim() ||
                            loading ||
                            // For non-marketing agents, require at least one completed file
                            (selectedAgentId !== "marketing_insights" && files.filter(f => f.status === 'completed').length === 0)
                        }
                        style={{
                            backgroundColor: "#1B5C29",
                            color: "white",
                            textTransform: "none",
                            fontWeight: 400,
                            borderRadius: "8px",
                            padding: "8px 24px",
                            fontSize: "15px",
                            height: "35px",
                            opacity: (!projectName.trim() || loading || (selectedAgentId !== "marketing_insights" && files.filter(f => f.status === 'completed').length === 0)) ? 0.5 : 1
                        }}
                    >
                        <span className="truncate pt-0.3">
                            {loading ? "Creating..." : "Create Project"}
                        </span>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

CreateProjectDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onProjectCreated: PropTypes.func.isRequired,
};
